import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Ticket as TicketIcon, CreditCard, ListChecks, ShieldWarning, Bank, Note, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { API } from '@/App';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'tickets', label: 'Tickets', icon: TicketIcon },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'ledger', label: 'Ledger', icon: ListChecks },
  { id: 'kyc', label: 'KYC', icon: ShieldWarning },
  { id: 'withdrawals', label: 'Withdrawals', icon: Bank },
  { id: 'risk', label: 'Risk', icon: ShieldWarning },
  { id: 'notes', label: 'Notes', icon: Note },
];

function StatusPill({ status }) {
  const color = {
    active: 'bg-green-900/30 text-green-300 border-green-500/30',
    suspended: 'bg-orange-900/30 text-orange-200 border-orange-500/40',
    anonymized: 'bg-red-900/30 text-red-200 border-red-500/40',
  }[status || 'active'] || 'bg-[#cba135]/10 text-[#cba135] border-[#cba135]/30';
  return <span className={`inline-block text-xs px-2 py-0.5 rounded border ${color}`}>{status || 'active'}</span>;
}

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toISOString().replace('T', ' ').slice(0, 19); } catch { return s; }
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#a8872b]">{label}</p>
      <p className="text-sm text-[#cba135] mt-1 break-words">{children ?? '—'}</p>
    </div>
  );
}

export default function UserDetailModal({ userId, token, currentAdminId, onClose, onMutated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [tab, setTab] = useState('profile');
  const [newNote, setNewNote] = useState('');

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/users/${userId}/detail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error(`Failed to load user: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const doAction = async (path, body = null, confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActionBusy(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(d.message || 'Done');
        await fetchDetail();
        onMutated && onMutated();
        return d;
      }
      toast.error(d.detail || `HTTP ${r.status}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleSuspend = () => {
    const reason = window.prompt('Reason for suspension (optional, max 500 chars)');
    if (reason === null) return;
    doAction(`/admin/users/${userId}/suspend`, { reason });
  };

  const handleUnsuspend = () => doAction(
    `/admin/users/${userId}/unsuspend`, null,
    `Reactivate ${data?.profile?.email}? They will be able to log in again.`,
  );

  const handleAnonymize = () => {
    const reason = window.prompt(
      `⚠ IRREVERSIBLE — ANONYMIZE USER\n\n` +
      `This replaces email, name, phone, address, IBAN and KYC docs with placeholders.\n` +
      `Ledger entries are kept for AML compliance.\n` +
      `The user can no longer log in.\n\n` +
      `Type a reason (e.g., "GDPR request 2026-05-13") to confirm:`,
    );
    if (!reason || !reason.trim()) {
      toast.info('Anonymize cancelled (reason required)');
      return;
    }
    doAction(`/admin/users/${userId}/anonymize`, { reason: reason.trim() });
  };

  // ─── Hard delete: 2-step confirmation flow ──────────────────────────────
  // Click 1 → arms the button (red "Confirm delete?" replaces "Delete account")
  // Click 2 → opens window.prompt requiring the admin to type DELETE_USER_<email>
  // Anywhere else / 8s timeout / different action click → cancels arming
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return undefined;
    const timer = setTimeout(() => setDeleteArmed(false), 8000);
    return () => clearTimeout(timer);
  }, [deleteArmed]);

  const handleDeleteClick = () => {
    if (!deleteArmed) {
      setDeleteArmed(true);
      toast.warning(
        `Account "${profile?.email}" marked for deletion. Click "Confirm delete?" again within 8 seconds to proceed.`,
      );
      return;
    }
    // Second click → run final confirmation prompt
    setDeleteArmed(false);
    const expected = `DELETE_USER_${profile?.email || ''}`;
    const typed = window.prompt(
      `⛔ HARD DELETE — IRREVERSIBLE\n\n` +
      `This will PERMANENTLY remove the user account from the database.\n\n` +
      `What gets deleted:\n` +
      `  • User profile (email, name, password, KYC files)\n` +
      `  • Pending payment intents\n` +
      `  • Risk flags on this user\n\n` +
      `What stays (for AML compliance, forever):\n` +
      `  • Ledger entries (financial integrity)\n` +
      `  • Transactions (wallet history)\n` +
      `  • Past tickets / draws\n` +
      `  • Audit log of THIS deletion\n\n` +
      `For GDPR right-to-erasure prefer "Anonymize" instead.\n\n` +
      `To proceed type the exact phrase below:\n${expected}`,
    );
    if (typed == null) {
      toast.info('Delete cancelled');
      return;
    }
    if (typed.trim() !== expected) {
      toast.error('Confirmation phrase does not match. Delete cancelled.');
      return;
    }
    const reason = window.prompt(
      'Reason for deletion (will be stored in audit log — optional, max 500 chars):',
    ) || '';
    doAction(`/admin/users/${userId}/delete`, { confirm: expected, reason: reason.trim() })
      .then((d) => {
        if (d) {
          // Account no longer exists — close the modal and let parent refresh the list
          setTimeout(() => onClose(), 800);
        }
      });
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset password for ${data?.profile?.email}? A one-time password will be generated.`)) return;
    setActionBusy(true);
    try {
      const r = await fetch(`${API}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok && d.oneTimePassword) {
        window.prompt(
          `One-time password for ${d.email}:\n\nCopy NOW — won't be shown again.\nUser will be forced to change on next login.`,
          d.oneTimePassword,
        );
        toast.success('Password reset. Share securely.');
        await fetchDetail();
      } else {
        toast.error(d.detail || 'Reset failed');
      }
    } finally {
      setActionBusy(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) { toast.error('Note text required'); return; }
    const result = await doAction(`/admin/users/${userId}/notes`, { note: newNote.trim() });
    if (result) setNewNote('');
  };

  const profile = data?.profile;
  const isAnonymized = profile?.accountStatus === 'anonymized';
  const isSuspended = profile?.accountStatus === 'suspended';
  const isCurrentAdmin = profile?.id === currentAdminId;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-[#0e0b04]/90 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
          className="bg-[#1a1508] border border-[#cba135]/30 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
          data-testid="user-detail-modal"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#cba135]/15">
            <div className="flex items-center gap-3">
              <User size={26} weight="duotone" className="text-[#cba135]" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="heading-font text-lg text-[#cba135]">{profile?.email || '...'}</h2>
                  {profile && <StatusPill status={profile.accountStatus || 'active'} />}
                  {profile?.role === 'admin' && (
                    <span className="text-xs px-2 py-0.5 rounded border bg-[#cba135]/15 text-[#cba135] border-[#cba135]/40">ADMIN</span>
                  )}
                </div>
                <p className="text-xs text-[#cba135]/50 mt-0.5">{profile?.id}</p>
              </div>
            </div>
            <button
              data-testid="close-user-detail"
              onClick={onClose}
              className="p-2 text-[#cba135]/60 hover:text-[#cba135] hover:bg-[#cba135]/10 rounded transition-colors"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>

          {profile && !isCurrentAdmin && profile.role !== 'admin' && (
            <div className="px-6 py-3 border-b border-[#cba135]/10 bg-[#0e0b04]/60 flex flex-wrap gap-2">
              {!isSuspended && !isAnonymized && (
                <Button data-testid="action-suspend" size="sm" variant="outline" onClick={handleSuspend} disabled={actionBusy}
                  className="border-orange-500/30 text-orange-200 hover:bg-orange-500/10">
                  Suspend
                </Button>
              )}
              {isSuspended && (
                <Button data-testid="action-unsuspend" size="sm" variant="outline" onClick={handleUnsuspend} disabled={actionBusy}
                  className="border-green-500/30 text-green-200 hover:bg-green-500/10">
                  Unsuspend
                </Button>
              )}
              {!isAnonymized && (
                <Button data-testid="action-anonymize" size="sm" variant="outline" onClick={handleAnonymize} disabled={actionBusy}
                  className="border-red-500/30 text-red-200 hover:bg-red-500/10">
                  Anonymize (GDPR)
                </Button>
              )}
              {!isAnonymized && (
                <Button data-testid="action-reset-pwd" size="sm" variant="outline" onClick={handleResetPassword} disabled={actionBusy}
                  className="border-[#cba135]/30 text-[#cba135] hover:bg-[#cba135]/10">
                  Reset password
                </Button>
              )}
              <Button
                data-testid="action-delete-account"
                size="sm"
                variant="outline"
                onClick={handleDeleteClick}
                disabled={actionBusy}
                className={
                  deleteArmed
                    ? 'border-red-500 text-red-100 bg-red-600/40 hover:bg-red-600/60 animate-pulse font-bold'
                    : 'border-red-700/40 text-red-300/80 hover:bg-red-900/30 hover:text-red-200'
                }
              >
                {deleteArmed ? 'Confirm delete?' : 'Delete account'}
              </Button>
            </div>
          )}

          <div className="flex border-b border-[#cba135]/10 overflow-x-auto bg-[#0e0b04]/40">
            {TABS.map((t) => {
              const Icon = t.icon;
              const count = data?.counts?.[t.id === 'risk' ? 'riskFlags' : t.id === 'kyc' ? null : t.id === 'profile' ? null : t.id === 'notes' ? 'adminNotes' : t.id];
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-all whitespace-nowrap ${
                    tab === t.id
                      ? 'border-[#cba135] text-[#cba135] bg-[#cba135]/5'
                      : 'border-transparent text-[#cba135]/60 hover:text-[#cba135] hover:bg-[#cba135]/5'
                  }`}
                >
                  <Icon size={16} weight={tab === t.id ? 'fill' : 'regular'} />
                  {t.label}
                  {count != null && count > 0 && (
                    <span className="text-xs bg-[#cba135]/20 px-1.5 rounded">{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading && <p className="text-[#cba135]/60">Loading…</p>}
            {!loading && profile && tab === 'profile' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="ID">{profile.id}</Field>
                <Field label="Email">{profile.email}</Field>
                <Field label="Name">{profile.name}</Field>
                <Field label="Username">{profile.username}</Field>
                <Field label="First name">{profile.firstName}</Field>
                <Field label="Last name">{profile.lastName}</Field>
                <Field label="Phone">{profile.phone}</Field>
                <Field label="Date of birth">{profile.dateOfBirth}</Field>
                <Field label="Country">{profile.country}</Field>
                <Field label="Address">{profile.address}</Field>
                <Field label="IBAN">{profile.bankAccountIban}</Field>
                <Field label="Role">{profile.role}</Field>
                <Field label="Account status"><StatusPill status={profile.accountStatus || 'active'} /></Field>
                <Field label="Verification">{profile.verificationStatus} (step {profile.verificationStep})</Field>
                <Field label="Email verified">{String(profile.emailVerified)}</Field>
                <Field label="Wallet balance">€{(profile.walletBalance ?? 0).toFixed(2)}</Field>
                <Field label="Locked balance">€{(profile.lockedBalance ?? 0).toFixed(2)}</Field>
                <Field label="Created">{fmtDate(profile.createdAt)}</Field>
                {isSuspended && (
                  <>
                    <Field label="Suspended at">{fmtDate(profile.suspendedAt)}</Field>
                    <Field label="Suspended by">{profile.suspendedBy}</Field>
                    <div className="md:col-span-2"><Field label="Reason">{profile.suspendReason}</Field></div>
                  </>
                )}
                {isAnonymized && (
                  <>
                    <Field label="Anonymized at">{fmtDate(profile.anonymizedAt)}</Field>
                    <Field label="Anonymized by">{profile.anonymizedBy}</Field>
                    <div className="md:col-span-2"><Field label="Reason">{profile.anonymizeReason}</Field></div>
                  </>
                )}
              </div>
            )}

            {!loading && tab === 'tickets' && (
              <DataTable rows={data?.tickets || []} cols={[
                { k: 'drawNumber', label: 'Draw' }, { k: 'number', label: 'Ticket #' },
                { k: 'status', label: 'Status' }, { k: 'createdAt', label: 'Created', fmt: fmtDate },
              ]} />
            )}

            {!loading && tab === 'payments' && (
              <DataTable rows={data?.payments || []} cols={[
                { k: 'provider', label: 'Provider' }, { k: 'status', label: 'Status' },
                { k: 'amount', label: 'Amount', fmt: (v) => `€${v}` },
                { k: 'createdAt', label: 'Created', fmt: fmtDate },
              ]} />
            )}

            {!loading && tab === 'ledger' && (
              <DataTable rows={data?.ledger || []} cols={[
                { k: 'type', label: 'Type' }, { k: 'amount', label: 'Amount', fmt: (v) => `€${v}` },
                { k: 'currency', label: 'Currency' }, { k: 'createdAt', label: 'When', fmt: fmtDate },
              ]} />
            )}

            {!loading && tab === 'kyc' && (
              <div className="space-y-3">
                {data?.kycCase ? (
                  <pre className="text-xs text-[#cba135]/85 bg-[#0e0b04] p-4 rounded border border-[#cba135]/15 overflow-x-auto">
{JSON.stringify(data.kycCase, null, 2)}
                  </pre>
                ) : (
                  <p className="text-[#cba135]/60">No KYC case on file.</p>
                )}
                <p className="text-xs text-[#cba135]/50">KYC document files live on disk under KYC_UPLOAD_DIR — not exposed via API.</p>
              </div>
            )}

            {!loading && tab === 'withdrawals' && (
              <DataTable rows={data?.withdrawals || []} cols={[
                { k: 'status', label: 'Status' }, { k: 'amount', label: 'Amount', fmt: (v) => `€${v}` },
                { k: 'iban', label: 'IBAN' }, { k: 'createdAt', label: 'When', fmt: fmtDate },
              ]} />
            )}

            {!loading && tab === 'risk' && (
              <DataTable rows={data?.riskFlags || []} cols={[
                { k: 'type', label: 'Type' }, { k: 'severity', label: 'Severity' },
                { k: 'status', label: 'Status' }, { k: 'createdAt', label: 'When', fmt: fmtDate },
              ]} />
            )}

            {!loading && tab === 'notes' && (
              <div className="space-y-4">
                <div className="bg-[#0e0b04] border border-[#cba135]/15 rounded p-3">
                  <Textarea
                    data-testid="new-note-textarea"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add an admin note (visible only to admin) — e.g., 'Verified KYC docs by phone 2026-05-13'"
                    rows={3}
                    maxLength={2000}
                    className="bg-transparent border-[#cba135]/20 text-[#cba135]"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-[#cba135]/50">{newNote.length} / 2000</span>
                    <Button
                      data-testid="add-note-button"
                      size="sm"
                      onClick={handleAddNote}
                      disabled={actionBusy || !newNote.trim()}
                      className="bg-[#cba135] text-[#0e0b04] hover:bg-[#d4b347]"
                    >
                      <FloppyDisk size={14} weight="bold" className="mr-1.5" />
                      Save note
                    </Button>
                  </div>
                </div>
                {(data?.adminNotes || []).length === 0 ? (
                  <p className="text-[#cba135]/60 text-sm">No admin notes yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.adminNotes.slice().reverse().map((n) => (
                      <li key={n.id} className="bg-[#0e0b04]/70 border border-[#cba135]/10 rounded p-3" data-testid="admin-note-item">
                        <p className="text-sm text-[#cba135] whitespace-pre-wrap">{n.text}</p>
                        <p className="text-xs text-[#cba135]/50 mt-1.5">{n.authorEmail} · {fmtDate(n.createdAt)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function DataTable({ rows, cols }) {
  if (!rows.length) return <p className="text-[#cba135]/60 text-sm">Nothing here yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#cba135]/15 text-[#a8872b] text-xs uppercase tracking-[0.15em]">
            {cols.map((c) => <th key={c.k} className="text-left py-2 px-2">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r._key || `${i}-${cols[0]?.k}-${r[cols[0]?.k] ?? 'row'}`} className="border-b border-[#cba135]/5 text-[#cba135]/90 hover:bg-[#cba135]/5">
              {cols.map((c) => (
                <td key={c.k} className="py-2 px-2 break-words">
                  {c.fmt ? c.fmt(r[c.k]) : (r[c.k] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
